// columns.ts
import * as React from "react";
import { GridColDef } from "@mui/x-data-grid";
import { Box, Button, Tooltip } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { formatDateTime, parseFormattedDate } from "../../../common/DateUtil";
import { RowActions } from "./RowActionsComponent";

type TFunc = (key: string) => string;

export type ColumnsDeps = {
    t: TFunc;
    isMobile: boolean;
    isHotelFlow?: boolean;
    navigateWithUuidTo: (entity: any, path: string) => void;
    handleOpenEditModal: (uuid: string) => void;
    downloadContracts: (uuid: string) => Promise<any>;
    requestValidate: (uuid: string) => void;
    requestCancel: (uuid: string) => void;
    markAdvanceReceived: (uuid: string) => Promise<any> | void;
};

// Gebruik een factory i.p.v. const columns = [...]
export function makeReservationColumns({
                                           t,
                                           isMobile,
                                           isHotelFlow,
                                           navigateWithUuidTo,
                                           handleOpenEditModal,
                                           downloadContracts,
                                           requestValidate,
                                           requestCancel,
                                           markAdvanceReceived,
                                       }: ColumnsDeps): GridColDef[] {
    // Helper to determine if a reservation is awaiting advance payment
    const isAwaitingAdvance = (row) => {
        const status = row?.status;
        return status === "BOOKED_AWAIT_ADVANCE";
    };
    const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

    return [
        {
            field: "warn",
            headerName: "",
            width: 42,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => {
                if (!isAwaitingAdvance(params.row)) return null;
                return (
                    <Tooltip
                        placement="right"
                        slotProps={{
                            tooltip: {
                                sx: {
                                    bgcolor: "#fff",
                                    color: "text.primary",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    boxShadow: 1,
                                },
                            },
                        }}
                        title={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <span>{t("awaiting_advance") || "Awaiting advance"}</span>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        markAdvanceReceived?.(params.row.uuid);
                                    }}
                                >
                                    {t("mark_advance_received") || "Mark received"}
                                </Button>
                            </Box>
                        }
                    >
                        <WarningAmberIcon color="warning" fontSize="small" />
                    </Tooltip>
                );
            },
        },
        {
            field: "checkIn",
            headerName: t("check_in"),
            flex: 1,
            minWidth: 140,
            valueGetter: (params) => {
                const fromDate = params.row.fromDate;
                const checkInHour = params.row.checkIn;
                return formatDateTime(fromDate, checkInHour);
            },
            sortComparator: (value1, value2) => {
                const date1 = parseFormattedDate(value1);
                const date2 = parseFormattedDate(value2);
                if (date1 < date2) return -1;
                if (date1 > date2) return 1;
                return 0;
            },
        },
        {
            field: "checkOut",
            headerName: t("checkOut"),
            flex: 1,
            minWidth: 140,
            valueGetter: (params) => {
                const toDate = params.row.toDate;
                const checkOut = params.row.checkOut;
                return formatDateTime(toDate, checkOut);
            },
            sortComparator: (value1, value2) => {
                const date1 = parseFormattedDate(value1);
                const date2 = parseFormattedDate(value2);
                if (date1 < date2) return -1;
                if (date1 > date2) return 1;
                return 0;
            },
        },
        {
            field: "verblijven",
            headerName: isHotelFlow ? t("stay_types") : t("service_name"),
            flex: 1,
            minWidth: 120,
            sortable: false,
            hide: isMobile,
            valueGetter: (params) => isHotelFlow ? `${params.row.stays?.map(stay => stay.name).join(", ") ?? ""}` : `${params.row.serviceName ?? ""}`,
        },
        {
            field: "price",
            headerName: t("price"),
            minWidth: 100,
            flex: 0.6,
            valueGetter: (params) => `${params.row.price} €`,
        },
        {
            field: "advance",
            headerName: `${t("advance")} *`,
            minWidth: 110,
            flex: 0.6,
            hide: isMobile,
            valueGetter: (params) => `${params.row.advance} €`,
        },
        {
            field: "aantal",
            headerName: t("cats"),
            minWidth: 140,
            sortable: false,
            flex: 1,
            valueGetter: (params) => `${params.row.animals?.map((c: any) => c.name).join(", ")}`,
            renderCell: (params) => {
                const animals = params.row.animals || [];
                return (
                    <span>
            {animals.map((animal: any) => (
                <Button
                    key={animal.id}
                    variant="text"
                    size="small"
                    onClick={() => navigateWithUuidTo(animal, "dieren")}
                    sx={{ mr: 0.5 }}
                >
                    {animal.name}
                </Button>
            ))}
          </span>
                );
            },
        },
        {
            field: "eigenaar",
            headerName: t("owner"),
            sortable: false,
            minWidth: 150,
            flex: 1,
            valueGetter: (params) => `${params.row.personInformation?.name}`,
            renderCell: (params) => {
                const personInformation = params.row.personInformation;
                return (
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => navigateWithUuidTo(personInformation, "klanten")}
                    >
                        {params.value}
                    </Button>
                );
            },
        },
        {
            field: "dierenarts",
            headerName: t("veterinarian"),
            sortable: false,
            minWidth: 150,
            flex: 1,
            hide: isMobile,
            valueGetter: (params) => `${params.row.veterinaryDto?.name ?? ""}`,
        },
        {
            field: "action",
            headerName: t("actions"),
            sortable: false,
            minWidth: 120,
            flex: 0.6,
            renderCell: (params) => (
                <RowActions
                    row={params.row}
                    t={t}
                    handleOpenEditModal={handleOpenEditModal}
                    downloadContracts={downloadContracts}
                    requestValidate={requestValidate}
                    requestCancel={requestCancel}
                />
            ),
        },
    ];
}
